import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchActivePromotion, PROMO_DISMISS_KEY } from "@/lib/promotions";
import type { Promotion } from "@/lib/promotions";

/**
 * Anuncio promocional a pantalla completa (solo móvil).
 * Se muestra una vez por sesión y es totalmente editable desde el panel
 * de administración. Incluye botón flotante de cierre accesible para el pulgar.
 */
export default function AdOverlay() {
  const navigate = useNavigate();
  const [promo, setPromo] = useState<Promotion | null>(null);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    let mounted = true;

    const dismissed = sessionStorage.getItem(PROMO_DISMISS_KEY);
    if (dismissed === "1") return;

    fetchActivePromotion().then((data) => {
      if (!mounted) return;
      if (data && data.image_url) {
        setPromo(data);
        // Pequeño retraso para que el blur-in se perciba tras cargar la app.
        window.setTimeout(() => setVisible(true), 250);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  const close = () => {
    setClosing(true);
    sessionStorage.setItem(PROMO_DISMISS_KEY, "1");
    window.setTimeout(() => {
      setVisible(false);
      setPromo(null);
      setClosing(false);
    }, 250);
  };

  const handleCta = () => {
    sessionStorage.setItem(PROMO_DISMISS_KEY, "1");
    setVisible(false);
    setPromo(null);
    const link = promo?.cta_link;
    if (link && link.startsWith("/")) {
      navigate(link);
    } else if (link) {
      window.open(link, "_blank", "noopener");
    }
  };

  if (!promo || !visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Promoción"
      onClick={close}
      className={`fixed inset-0 z-[90] md:hidden ${
        closing
          ? "opacity-0 transition-opacity duration-300 ease-out"
          : "animate-blur-in"
      }`}
    >
      {/* Imagen de fondo */}
      {promo.image_url && (
        <img
          src={promo.image_url}
          alt={promo.title ?? "Promoción VendeYa"}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
      )}

      {/* Overlay de gradiente para legibilidad */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/80"></div>

      {/* Botón de cierre (accesible para el pulgar) */}
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          close();
        }}
        aria-label="Cerrar promoción"
        className="glass absolute right-4 top-4 z-20 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full text-white transition-transform active:scale-90"
      >
        <i className="ri-close-line text-2xl"></i>
      </button>

      {/* Contenido */}
      <div className="pointer-events-none relative z-10 flex h-full flex-col justify-end px-6 pb-12">
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
          <i className="ri-sparkling-2-line text-accent-300"></i>
          Oferta exclusiva
        </span>
        <h2 className="mt-3 font-heading text-3xl font-bold leading-tight text-white">
          {promo.title ?? "Oferta especial"}
        </h2>
        {promo.subtitle && (
          <p className="mt-2 max-w-xs text-sm leading-relaxed text-white/85">
            {promo.subtitle}
          </p>
        )}

        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            handleCta();
          }}
          className="pointer-events-auto mt-6 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-white px-6 py-3.5 text-base font-semibold text-foreground-950 transition-transform active:scale-95 dark:text-foreground-50"
        >
          {promo.cta_text ?? "Ver promoción"}
          <i className="ri-arrow-right-line"></i>
        </button>
      </div>
    </div>
  );
}